/**
 * @module editablefield/ui/editablefieldformview
 */

import { icons } from 'ckeditor5/src/core';
import {
	ButtonView, createLabeledInputText, FocusCycler, injectCssTransitionDisabler, LabeledFieldView, submitHandler, View,
	ViewCollection
} from 'ckeditor5/src/ui';
import { FocusTracker, KeystrokeHandler } from 'ckeditor5/src/utils';
import '../../theme/editablefieldform.pcss';


/**
 * The editablefield form view controller class.
 *
 * @extends View
 */
export default class EditableFieldFormView extends View {
	/**
	 * @inheritDoc
	 */
	constructor( locale ) {
		super( locale );

		const t = locale.t;

		/**
		 * @readonly
		 */
		this.focusTracker = new FocusTracker();

		/**
		 * @readonly
		 */
		this.keystrokes = new KeystrokeHandler();

		/**
		 * @member {LabeledInputView}
		 */
		this.valueInputView = this._createInput( t( 'Valor' ), t( 'Valor do campo' ) );
		/**
		 * @member {LabeledInputView}
		 */
		this.placeholderInputView = this._createInput( t( 'Descrição' ), t( 'Descrição do campo' ) );

		this.saveButtonView = this._createButton( t( 'Salvar' ), icons.check, 'ck-button-save' );
		this.saveButtonView.type = 'submit';
		this.cancelButtonView = this._createButton( t( 'Cancelar' ), icons.cancel, 'ck-button-cancel', 'cancel' );

		/**
		 * @readonly
		 * @protected
		 */
		this._focusables = new ViewCollection();

		/**
		 * @readonly
		 * @protected
		 */
		this._focusCycler = new FocusCycler( {
			focusables: this._focusables,
			focusTracker: this.focusTracker,
			keystrokeHandler: this.keystrokes,
			actions: {
				// Navigate form fields backwards using the Shift + Tab keystroke.
				focusPrevious: 'shift + tab',

				// Navigate form fields forwards using the Tab key.
				focusNext: 'tab'
			}
		} );

		this.setTemplate( {
			tag: 'form',

			attributes: {
				class: [ 'ck', 'ck-editablefield-form', 'ck-responsive-form' ],
				tabindex: '-1'
			},

			children: [
				this.valueInputView,
				this.placeholderInputView,
				{
					tag: 'div',
					attributes: {
						class: [ 'buttons' ]
					},
					children: [
						this.saveButtonView,
						this.cancelButtonView
					]
				}
			]
		} );

		injectCssTransitionDisabler( this );
	}

	/**
	 * @inheritDoc
	 */
	render() {
		super.render();

		submitHandler( {
			view: this
		} );

		const childViews = [
			this.valueInputView,
			this.placeholderInputView,
			this.saveButtonView,
			this.cancelButtonView
		];

		childViews.forEach( v => {
			this._focusables.add( v );
			this.focusTracker.add( v.element );
		} );
		this.keystrokes.listenTo( this.element );
	}

	focus() {
		this._focusCycler.focusFirst();
	}

	/**
	 * Creates a labeled input view.
	 *
	 * @private
	 * @param {String} label The button label.
	 * @param {String} placeholder The button label.
	 * @returns {LabeledFieldView} Labeled input view instance.
	 */
	_createInput( label, placeholder ) {
		const t = this.locale.t;
		const labeledInput = new LabeledFieldView( this.locale, createLabeledInputText );

		labeledInput.label = label;
		labeledInput.placeholder = placeholder;

		return labeledInput;
	}

	/**
	 * Creates a button view.
	 *
	 * @private
	 * @param {String} label The button label.
	 * @param {String} icon The button's icon.
	 * @param {String} className The additional button CSS class name.
	 * @param {String} [eventName] An event name that the `ButtonView#execute` event will be delegated to.
	 * @returns {ButtonView} The button view instance.
	 */
	_createButton( label, icon, className, eventName ) {
		const button = new ButtonView( this.locale );

		button.set( {
			label,
			icon,
			tooltip: true
		} );

		button.extendTemplate( {
			attributes: {
				class: className
			}
		} );

		if ( eventName ) {
			button.delegate( 'execute' ).to( this, eventName );
		}

		return button;
	}
}
